// Timezone-aware scheduling helpers for On This Rock consultations.
import { AVAILABILITY } from "../data";

// Returns the UTC Date instant for a given wall-clock time in a specific IANA timezone.
function zonedWallTimeToUTC(year, month, day, hour, minute, timeZone) {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(new Date(utcGuess)).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  let h = parseInt(parts.hour, 10);
  if (h === 24) h = 0;
  const asUTC = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    h,
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10)
  );
  const offset = asUTC - utcGuess;
  return new Date(utcGuess - offset);
}

// Build the next `daysAhead` worth of available consultation slots (owner timezone).
export function generateAvailability(daysAhead = 24) {
  const { days, startHour, endHour, slotMinutes, ownerTimeZone } = AVAILABILITY;
  const today = new Date();
  const dateMap = [];

  for (let i = 1; i <= daysAhead; i++) {
    const d = new Date(today.getTime() + i * 86400000);
    // Determine weekday in owner's timezone
    const wdName = new Intl.DateTimeFormat("en-US", {
      timeZone: ownerTimeZone,
      weekday: "short",
    }).format(d);
    const wdIndex = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(wdName);
    if (!days.includes(wdIndex)) continue;

    // Owner-timezone calendar date for this day
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: ownerTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .format(d)
      .split("-");
    const [year, month, day] = parts.map((n) => parseInt(n, 10));

    const slots = [];
    for (let m = startHour * 60; m <= endHour * 60; m += slotMinutes) {
      const hh = Math.floor(m / 60);
      const mm = m % 60;
      const instant = zonedWallTimeToUTC(year, month, day, hh, mm, ownerTimeZone);
      if (instant.getTime() > Date.now()) {
        slots.push(instant.toISOString());
      }
    }
    if (slots.length) {
      dateMap.push({ year, month, day, slots });
    }
  }
  return dateMap;
}

export function formatDateLabel(year, month, day, timeZone) {
  // Build a noon instant for that owner-date to label safely
  const ref = new Date(Date.UTC(year, month - 1, day, 17, 0, 0));
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(ref);
}

export function formatTime(iso, timeZone) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(iso));
}

export function tzAbbr(timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "short",
    hour: "numeric",
  }).formatToParts(new Date());
  const z = parts.find((p) => p.type === "timeZoneName");
  return z ? z.value : "";
}

export function detectTimeZone(options) {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (options.some((o) => o.value === tz)) return tz;
  } catch (e) {
    console.warn("Timezone detection failed, defaulting to Central:", e);
  }
  return "America/Chicago";
}
